import { useEffect, useState } from "react";
import { Loader2, LogOut, MonitorSmartphone, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../../connect/api";
import { ago, Avatar, Empty, Header, roleLine, Spinner } from "../ui";

export const AdminSessions = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/sessions").then((r) => setD(r.data)).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, []);
  const signOut = async (s) => {
    if (!window.confirm(`Sign out ${s.staff.name}'s ${s.device}? They will need to log in again on that device.`)) return;
    setBusy(s.sid);
    try { await api.delete(`/admin/sessions/${s.sid}`); toast.success(`${s.device} signed out`); await load(); } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const signOutAll = async (email, name, n) => {
    if (!window.confirm(`Sign ${name} out of all ${n} devices?`)) return;
    setBusy(email);
    try { const { data } = await api.post("/admin/sessions/sign-out-all", { email }); toast.success(`${data.signed_out} device${data.signed_out === 1 ? "" : "s"} signed out`); await load(); } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const groups = d ? Object.values(d.items.reduce((acc, s) => { (acc[s.email] ||= { staff: s.staff, email: s.email, sessions: [] }).sessions.push(s); return acc; }, {})) : [];
  return (
    <div className="h-full flex flex-col">
      <Header title="Signed-in devices" sub={d ? `${d.total} active session${d.total === 1 ? "" : "s"}` : ""} back="/connect/admin" />
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" data-testid="admin-sessions">
        <p className="text-[11px] text-slate-500 px-1">Every phone and browser currently logged in to Connect. Sessions never expire on their own — sign one out here and that device returns to the login screen on its next action.</p>
        {!d ? <div className="flex justify-center py-10"><Spinner /></div> : groups.length === 0 ? <Empty icon={MonitorSmartphone} title="No active sessions" text="Devices appear here after their next login." /> : (
          <ul className="space-y-2">
            {groups.map((g) => (
              <li key={g.email} className="nxc-card p-3" data-testid={`session-group-${g.email}`}>
                <div className="flex items-center gap-3">
                  <Avatar name={g.staff.name} src={g.staff.avatar} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-100 truncate">{g.staff.name}</div>
                    <div className="text-[11px] text-slate-400 truncate">{roleLine(g.staff)}</div>
                  </div>
                  {g.sessions.length > 1 && (
                    <button onClick={() => signOutAll(g.email, g.staff.name, g.sessions.length)} disabled={!!busy} className="nxc-btn nxc-btn-ghost h-8 text-[11px] shrink-0" data-testid={`session-signout-all-${g.email}`}>{busy === g.email ? <Loader2 size={12} className="animate-spin" /> : <LogOut size={12} />} All ({g.sessions.length})</button>
                  )}
                </div>
                <ul className="mt-2 divide-y divide-white/5">
                  {g.sessions.map((s) => (
                    <li key={s.sid} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`session-row-${s.sid}`}>
                      <Smartphone size={14} className={s.channel === "mobile" ? "text-emerald-300 shrink-0" : "text-sky-300 shrink-0"} />
                      <div className="flex-1 min-w-[140px]">
                        <div className="text-xs text-slate-200">{s.device}{s.current ? <span className="ml-1.5 text-[10px] text-[var(--nxc-gold)] uppercase tracking-wider">this device</span> : null}</div>
                        <div className="text-[11px] text-slate-500">active {ago(s.last_seen)} · signed in {ago(s.created_at)}{s.ip ? ` · ${s.ip}` : ""}</div>
                      </div>
                      <button onClick={() => signOut(s)} disabled={!!busy} className="nxc-btn nxc-btn-ghost h-8 text-[11px] shrink-0 !text-red-300" data-testid={`session-signout-${s.sid}`}>{busy === s.sid ? <Loader2 size={12} className="animate-spin" /> : <LogOut size={12} />} Sign out</button>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
